import React, { useState, useEffect } from "react";
import { GoogleLogin } from '@react-oauth/google';
import { useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import leftImage from './images/banniere.jpg';
import useAuthCon from "../dashboard/useAthCon";
import LoadingSpinner from "./loading";
import { LinkedIn } from 'react-linkedin-login-oauth2';
import { FaChevronCircleDown, FaRegArrowAltCircleDown, FaEnvelope, FaSignInAlt, FaHandshake } from "react-icons/fa";
import { getAuthToken } from "../../authToken";
import CookieConsent from "./CookieConsent";

const Connexion = () => {
  useAuthCon();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formation, setFormation] = useState([]);
  const [filteredFormations, setFilteredFormations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [cookiesAccepted, setCookiesAccepted] = useState(false);
  const [showCookieModal, setShowCookieModal] = useState(false);

  const handleCookieAccept = (preferences) => {
    console.log('Préférences cookies acceptées:', preferences);
    setCookiesAccepted(true);

    // Vous pouvez envoyer ces préférences à votre backend
    const consentStatus = localStorage.getItem('cookieConsent');
    const savedPrefs = localStorage.getItem('cookiePreferences');

    // Stockez les préférences pour les utiliser dans les requêtes
    sessionStorage.setItem('userCookiePrefs', savedPrefs);
  };


  const handleCookieDecline = (preferences) => {
    console.log('Préférences cookies refusées:', preferences);
    setCookiesAccepted(false);
  };

  // État pour le formulaire de connexion manuelle
  const [showManualLogin, setShowManualLogin] = useState(false);
  const [loginData, setLoginData] = useState({
    email: '',
    first_name: '',
    last_name: ''
  });

  // Fonctions utilitaires pour les dates (garder votre code existant)
  const isValidDate = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date);
  };

  const normalizeDate = (dateString) => {
    if (!dateString) return null;

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      const [day, month, year] = dateString.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    const date = new Date(dateString);
    if (isValidDate(date)) {
      return date.toISOString().split('T')[0];
    }

    return null;
  };

  const isDateList = (periode) => {
    if (!periode) return false;

    if (typeof periode === 'string') {
      try {
        const cleanedPeriode = periode.replace(/'/g, '"');
        const parsed = JSON.parse(cleanedPeriode);
        return Array.isArray(parsed) && parsed.length > 0;
      } catch (error) {
        return false;
      }
    }

    return Array.isArray(periode) && periode.length > 0;
  };

  const getAllDatesDisplay = (periode) => {
    if (!periode || periode === '[]' || periode === '""' || periode === "''") {
      return <strong className="text-muted">A la demande</strong>;
    }

    if (isDateList(periode)) {
      try {
        let dates;
        if (typeof periode === 'string') {
          const cleanedPeriode = periode.replace(/'/g, '"');
          dates = JSON.parse(cleanedPeriode);
        } else {
          dates = periode;
        }

        const validDates = dates
          .map(date => normalizeDate(date))
          .filter(date => date !== null && isValidDate(new Date(date)))
          .sort();

        if (validDates.length > 0) {
          return (
            <strong className="">
              {validDates.map((date, index) => {
                const dateObj = new Date(date);
                return (
                  <strong key={index}>
                    {dateObj.toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: '2-digit'
                    })}
                    {index < validDates.length - 1 && ', '}
                  </strong>
                );
              })}
            </strong>
          );
        }
      } catch (error) {
        console.warn('Erreur de parsing des dates:', error);
      }
    }

    if (typeof periode === 'string' && periode.trim() !== '') {
      return (
        <strong className="text-muted small">
          {periode}
        </strong>
      );
    }

    return '-';
  };

  const parseFormationDate = (dateString) => {
    if (!dateString) return new Date();

    try {
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return new Date(dateString);
      }

      if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        const [day, month, year] = dateString.split('/').map(Number);
        return new Date(year, month - 1, day);
      }

      return new Date(dateString);
    } catch (error) {
      console.warn(`Erreur de parsing de date: ${dateString}`, error);
      return new Date();
    }
  };

  // Fonction pour filtrer les formations
  const filterFormations = (formations, search) => {
    if (!search.trim()) return formations;

    const searchLower = search.toLowerCase();

    // Fonction utilitaire pour formater les dates en format comparable (ex: 2025-11-08)
    const normalize = (value) => {
      if (!value) return '';
      if (value.includes('/')) {
        const [day, month, year] = value.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
      return value.trim();
    };

    return formations.filter((formation) => {
      const nomMatch = formation.nom.toLowerCase().includes(searchLower);
      const lieuMatch = formation.lieu.toLowerCase().includes(searchLower);

      // Vérif sur la date principale
      const dateMatch =
        formation.date &&
        normalize(formation.date).toLowerCase().includes(normalize(searchLower));

      // Vérif sur la période (tableau de dates ou string JSON)
      let periodeMatch = false;
      if (formation.periode) {
        try {
          let periodeArray =
            typeof formation.periode === 'string'
              ? JSON.parse(formation.periode.replace(/'/g, '"'))
              : formation.periode;

          periodeMatch = periodeArray.some((d) =>
            normalize(d).toLowerCase().includes(normalize(searchLower))
          );
        } catch (err) {
          console.warn('Erreur parsing période:', err);
        }
      }

      return nomMatch || lieuMatch || dateMatch || periodeMatch;
    });
  };

  // Appliquer le filtre quand le terme de recherche ou les formations changent
  useEffect(() => {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    const upcomingFormations = formation.filter(formation => {
      const formationDate = parseFormationDate(formation.date);
      formationDate.setHours(0, 0, 0, 0);
      return formationDate >= currentDate;
    });

    const filtered = filterFormations(upcomingFormations, searchTerm);
    setFilteredFormations(filtered);
  }, [formation, searchTerm]);




  const detectBrowser = () => {
    const userAgent = navigator.userAgent;

    if (userAgent.indexOf("Edg") > -1) {
      return "Microsoft Edge";
    } else if (userAgent.indexOf("Chrome") > -1 && userAgent.indexOf("Edg") === -1) {
      return "Google Chrome";
    } else if (userAgent.indexOf("Firefox") > -1) {
      return "Mozilla Firefox";
    } else if (userAgent.indexOf("Safari") > -1 && userAgent.indexOf("Chrome") === -1) {
      return "Apple Safari";
    } else if (userAgent.indexOf("Opera") > -1 || userAgent.indexOf("OPR") > -1) {
      return "Opera";
    } else if (userAgent.indexOf("Trident") > -1) {
      return "Internet Explorer";
    } else {
      return "Navigateur inconnu";
    }
  };

  // Fonction pour la connexion manuelle
  const handleManualLogin = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError('');
    const browser = detectBrowser();

    try {
      const response = await fetch(process.env.REACT_APP_HOTE + '/connexion-google/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: loginData.email,
          given_name: loginData.first_name,
          family_name: loginData.last_name,
          browser: browser
        }),
      });

      const result = await response.json();
      console.log("Résultat connexion manuelle:", result);

      if (response.ok) {
        const userId = result.user_id;
        // const tokenCle = result.acces;
        const tokenCle = await getAuthToken();
        const group_id = result.group_id;

        localStorage.setItem('access_token', tokenCle);

        if (userId) {
          localStorage.setItem('user_id', userId);
          localStorage.setItem('groupe', group_id);
          navigate(`/activation-page`, {
            state: {
              user: {
                email: loginData.email,
                given_name: loginData.first_name,
                family_name: loginData.last_name
              },
              userId
            }
          });
        } else {
          setError("L'ID utilisateur est manquant.");
        }
      } else {
        const userId = result.user_id;
        const tokenCle = result.acces;
        const group_id = result.group_id;

        if (userId) {
          localStorage.setItem('access_token', tokenCle);
          localStorage.setItem('user_id', userId);
          localStorage.setItem('groupe', JSON.stringify(group_id));
          const redirectTo = localStorage.getItem('redirectAfterLogin') || 'dashboard';
          localStorage.removeItem('redirectAfterLogin');
          navigate(redirectTo);
        } else {
          setError(result.message);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la connexion manuelle:', error);
      setError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  // Votre code existant pour Google Login
  const handleGoogleLogin = async (credentialResponse) => {
    const consentStatus = localStorage.getItem('cookieConsent');
    const cookiePrefs = localStorage.getItem('cookiePreferences');

  //   if (
  //     consentStatus !== 'accepted' &&
  //     consentStatus !== 'customized'
  //   ) {
  //     setError('Vous devez accepter ou configurer les cookies pour vous connecter.');
  // setShowCookieModal(true);
  //     return;
  //   }
    setIsLoading(true);
    try {
      const decoded = jwtDecode(credentialResponse?.credential);
      const user = decoded;
      const browser = detectBrowser();

      const response = await fetch(process.env.REACT_APP_HOTE + '/connexion-google/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user.email,
          given_name: user.given_name,
          family_name: user.family_name,
          browser: browser
        }),
      });

      const result = await response.json();
      console.log(result)

      if (response.ok) {
        const userId = result.user_id;
        // const tokenCle = result.acces;
        const tokenCle = await getAuthToken();
        const group_id = result.group_id;

        // localStorage.setItem('access_token', tokenCle);

        if (userId) {
          localStorage.setItem('user_id', userId);
          localStorage.setItem('groupe', group_id);
          navigate(`/activation-page`, { state: { user, userId } });
        } else {
          setError("L'ID utilisateur est manquant.");
        }
      } else {
        const userId = result.user_id;
        const tokenCle = await getAuthToken();
        const group_id = result.group_id;

        if (userId) {
          localStorage.setItem('access_token', tokenCle);
          localStorage.setItem('user_id', userId);
          localStorage.setItem('groupe', JSON.stringify(group_id));
          const redirectTo = localStorage.getItem('redirectAfterLogin') || 'dashboard';
          localStorage.removeItem('redirectAfterLogin');
          navigate(redirectTo);
        } else {
          setError(result.message);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la connexion Google:', error);
      setError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const errorMessage = (error) => {
    console.log('Erreur de connexion Google:', error);
    setError('Échec de la connexion avec Google. Veuillez réessayer.');
  };

  // Fetching formations data from API
  const fetchFormation = async () => {
    try {
      const response = await fetch(process.env.REACT_APP_HOTE + '/liste-formation/');
      if (response.ok) {
        const data = await response.json();
        const formationActive = data.filter(formation => formation.statut === "actif");
        setFormation(formationActive);
      } else {
        console.error('Erreur lors de la récupération des formations');
      }
    } catch (error) {
      console.error('Erreur réseau:', error);
    }
  };

  useEffect(() => {
    fetchFormation();
  }, []);

  const handleLinkedInSuccess = async (code) => {
    setIsLoading(true);
    console.log("code : ", code)
    try {
      const response = await fetch(process.env.REACT_APP_HOTE + '/connexion-linkedin/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });

      const result = await response.json();

      if (response.ok) {
        const userId = result.user_id;
        const tokenCle = result.acces;
        const group_id = result.group_id;

        localStorage.setItem('access_token', tokenCle);
        localStorage.setItem('user_id', userId);
        localStorage.setItem('groupe', group_id);

        navigate(`/activation-page`, { state: { user: result.user, userId } });
      } else {
        setError(result.message || "Erreur d'authentification LinkedIn");
      }
    } catch (err) {
      console.error("Erreur LinkedIn:", err);
      setError("Erreur lors de la connexion LinkedIn");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLinkedInFailure = (error) => {
    console.error("LinkedIn Error:", error);
    setError("Échec de l'authentification LinkedIn");
  };

  // Appliquer le style maxHeight conditionnellement
  const scrollStyle = {
    maxHeight: filteredFormations.length > 3 ? '230px' : 'auto',
    overflowY: filteredFormations.length > 3 ? 'auto' : 'visible'
  };

  const [sortOrder, setSortOrder] = useState('recent');

  // Fonction pour trier les formations par date
  const sortFormationsByDate = (formations, order) => {
    return [...formations].sort((a, b) => {
      const rawA = a.date_debut;
      const rawB = b.date_debut;

      if (!rawA && rawB) return 1;
      if (!rawB && rawA) return -1;
      if (!rawA && !rawB) return 0;

      const dateA = parseFormationDate(rawA || a.date);
      const dateB = parseFormationDate(rawB || b.date);

      if (order === 'recent') {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });
  };

  // Fonction pour changer l'ordre de tri
  const toggleSortOrder = () => {
    const newOrder = sortOrder === 'recent' ? 'ancien' : 'recent';
    setSortOrder(newOrder);
  };

  // Modifier l'useEffect pour inclure le tri
  useEffect(() => {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    const upcomingFormations = formation.filter(formation => {
      const formationDate = parseFormationDate(formation.date);
      formationDate.setHours(0, 0, 0, 0);
      return formationDate >= currentDate;
    });

    const filtered = filterFormations(upcomingFormations, searchTerm);
    const sorted = sortFormationsByDate(filtered, sortOrder);
    setFilteredFormations(sorted);
  }, [formation, searchTerm, sortOrder]);


  const handleDevenirPartenaire = () => {
    navigate('/Devenir-partenaire');
  };

  return (
    <>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          justifyContent: 'center',
          alignItems: 'center',
          // padding: '20px'
        }}>
          <div className="loginColumns animated fadeInDown" style={{
            width: '100%',
            // maxWidth: '1140px',
            margin: '0',
            borderRadius: '10px',
            overflow: 'hidden',
          }}>
            <div className="row complet" style={{
              margin: 0,
              backgroundColor: '#ffffff'
            }}>
              {/* Colonne gauche avec logo et options de connexion */}
              <div className="col-md-6 mb-0 gauche" style={{
                // padding: '30px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}>
                <div className="mt-4" style={{ textAlign: 'center' }}>

                  {error && <p className="alert alert-danger">{error}</p>}

                  {/* Options de connexion */}
                  <div className="">
                    {!showManualLogin ? (
                      <>
                        <img src={leftImage} alt="Logo" style={{
                          width: '100%',
                          height: 'auto',
                          maxHeight: '200px',
                          objectFit: 'cover',
                          marginBottom: '20px',
                          borderRadius: '8px'
                        }} />
                        <h2 className="font-bold">eTrainApp</h2>
                        <p className="mt-3">
                          La boîte à outils indispensable pour préparer et réussir sa certification...
                        </p>
                        <div className="d-flex justify-content-center align-items-center mb-3">
                          <GoogleLogin onSuccess={handleGoogleLogin} onError={errorMessage} />
                        </div>


                        <div className="position-relative text-center my-3">
                          <hr className="w-100" style={{ opacity: 0.3 }} />
                          {/* <span className="position-absolute bg-white px-3" style={{
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: '#6c757d',
                            fontSize: '13px'
                          }}>
                            ou
                          </span> */}
                        </div>

                        {/* Bouton Devenir partenaire */}
                        {/* <div className="text-center mt-2 mb-4">
                          <button
                            onClick={handleDevenirPartenaire}
                            className="btn btn-outline-primary btn-block d-flex align-items-center justify-content-center"
                            style={{ maxWidth: '240px', margin: '0 auto' }}
                          >
                            <FaHandshake className="mr-2" />
                            Devenir partenaire
                          </button>
                        </div> */}

                        {/* <div className="text-center mt-3">
                          <p className="mb-2">Ou</p>
                          <button
                            onClick={() => setShowManualLogin(true)}
                            className="btn btn-outline-primary btn-block d-flex align-items-center justify-content-center mb-4"
                            style={{ maxWidth: '260px', margin: '0 auto' }}
                          >
                            <FaEnvelope className="mr-2" />
                            Se connecter
                          </button>
                        </div> */}
                      </>
                    ) : (
                      // Formulaire de connexion manuelle
                      <div className="manual-login-form">
                        <form onSubmit={handleManualLogin}>

                          <div className="form-group mb-4 text-left">
                            <label htmlFor="last_name" className="form-label d-block text-left">
                              Nom <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              id="last_name"
                              className="form-control"
                              placeholder="Votre nom"
                              value={loginData.last_name}
                              onChange={(e) => setLoginData({ ...loginData, last_name: e.target.value })}
                            />
                          </div>

                          <div className="form-group mb-3 text-left">
                            <label htmlFor="first_name" className="form-label d-block text-left">
                              Prénom <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              id="first_name"
                              className="form-control"
                              placeholder="Votre prénom"
                              value={loginData.first_name}
                              onChange={(e) => setLoginData({ ...loginData, first_name: e.target.value })}
                            />
                          </div>

                          <div className="form-group mb-3 text-left">
                            <label htmlFor="email" className="form-label d-block text-left">
                              Email <span className="text-danger">*</span>
                            </label>
                            <input
                              type="email"
                              id="email"
                              className="form-control"
                              placeholder="votre@email.com"
                              required
                              value={loginData.email}
                              onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                            />
                          </div>

                          <div className="d-flex justify-content-between">
                            <button
                              type="button"
                              onClick={() => setShowManualLogin(false)}
                              className="btn btn-outline-secondary"
                            >
                              Retour
                            </button>
                            <button
                              type="submit"
                              className="btn btn-success"
                              disabled={isLoading}
                            >
                              {isLoading ? 'Connexion...' : 'Se connecter'}
                            </button>
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Colonne droite avec les formations */}
              <div className="col-md-6 mb-0 droite" style={{
                // padding: '30px',
                borderLeft: '1px solid #e7eaec',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <div>
                  <div className="d-flex justify-content-center align-items-center mb-3">
                    <h2 className="mb-0">
                      <b>Formations à venir</b>
                    </h2>
                  </div>

                  {/* Champ de recherche avec icône de tri */}
                  <div className="mb-3 position-relative">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Rechercher par nom ou lieu..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        borderRadius: '10px',
                        paddingLeft: '30px',
                        paddingRight: '40px'
                      }}
                    />
                    <i className="fa fa-search position-absolute" style={{
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#6c757d'
                    }}></i>

                    <i
                      className={`fa fa-sort-amount-${sortOrder === 'recent' ? 'asc' : 'desc'} position-absolute`}
                      style={{
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        cursor: 'pointer',
                        fontSize: '16px'
                      }}
                      onClick={toggleSortOrder}
                      title={sortOrder === 'recent' ? 'Du plus récent au plus ancien' : 'Du plus ancien au plus récent'}
                    ></i>
                  </div>

                  {/* Liste des formations */}
                  <div className="feed-activity pb-3" style={scrollStyle}>
                    {filteredFormations.length > 0 ? (
                      filteredFormations.map((formation, index) => (
                        <div key={formation.id} className="pb-0">
                          {index > 0 && <hr />}
                          <div className="feed-element pb-0">
                            <a href="/#" className="float-left mt-2">
                              <img
                                src={`${process.env.REACT_APP_HOTE}${formation.photo_url}`}
                                alt="Logo"
                                className="rounded-circle"
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  objectFit: 'cover',
                                  borderRadius: '5px',
                                }}
                              />
                            </a>
                            <div className="media-body">
                              {getAllDatesDisplay(formation.periode)} <br />
                              <i className="fa fa-file mr-1"></i>
                              <span> {formation.nom} </span> <br />
                              <i className="fa fa-map-marker mr-1"></i>
                              Lieu: <span> {formation.lieu} </span>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-center">
                        {searchTerm ? 'Aucune formation trouvée avec ce critère.' : 'Aucune formation à venir.'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div
              className="ibox-footer full-w mt-2 d-flex align-items-center justify-content-between pl-3 pr-3"
              style={{
                textAlign: 'center',
                width: '100%',
                borderTop: '1px solid #e7eaec',
                backgroundColor: '#f9f9f9',
                // padding: '15px 20px'
              }}
            >
              <small>© {new Date().getFullYear()} <strong>Inovact</strong>. Tous droits réservés.</small>

              <div className="d-flex align-items-center justify-content-between">
                <small className="me-3 text-muted mr-2">Nous suivre</small>
                <div className="d-flex align-items-center gap-3">
                  <a href="https://www.facebook.com/inovact/" className="text-decoration-none social-icon d-flex align-items-center justify-content-center mr-2"
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#4c4d4e10',
                      border: '1px solid #4c4d4e10',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#6c757d';
                      e.currentTarget.style.borderColor = '#6c757d';
                      e.currentTarget.querySelector('i').style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#4c4d4e10';
                      e.currentTarget.style.borderColor = '#4c4d4e10';
                      e.currentTarget.querySelector('i').style.color = '#6c757d';
                    }}
                    title="Facebook">
                    <i className="fa fa-facebook-f" style={{ fontSize: '12px', color: '#6c757d', transition: 'color 0.3s ease' }}></i>
                  </a>
                  <a href="https://x.com/Inovact_Sarl" className="text-decoration-none social-icon d-flex align-items-center justify-content-center mr-2"
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#4c4d4e10',
                      border: '1px solid #4c4d4e10',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#6c757d';
                      e.currentTarget.style.borderColor = '#6c757d';
                      e.currentTarget.querySelector('i').style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#4c4d4e10';
                      e.currentTarget.style.borderColor = '#4c4d4e10';
                      e.currentTarget.querySelector('i').style.color = '#6c757d';
                    }}
                    title="Twitter">
                    <i className="fa fa-twitter" style={{ fontSize: '12px', color: '#6c757d', transition: 'color 0.3s ease' }}></i>
                  </a>
                  <a href="https://www.linkedin.com/company/inovact" className="text-decoration-none social-icon d-flex align-items-center justify-content-center mr-2"
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#4c4d4e10',
                      border: '1px solid #4c4d4e10',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#6c757d';
                      e.currentTarget.style.borderColor = '#6c757d';
                      e.currentTarget.querySelector('i').style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#4c4d4e10';
                      e.currentTarget.style.borderColor = '#4c4d4e10';
                      e.currentTarget.querySelector('i').style.color = '#6c757d';
                    }}
                    title="LinkedIn">
                    <i className="fa fa-linkedin" style={{ fontSize: '12px', color: '#6c757d', transition: 'color 0.3s ease' }}></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <CookieConsent
  forceOpen={showCookieModal}
            onAccept={handleCookieAccept}
            onDecline={handleCookieDecline}
          />
        </div>
      )}
    </>
  );
};

export default Connexion;